// Phase P.37 — Unified Ledger page.
//
// Wallet selector at the top; renders the existing per-wallet page inside
// (ManagerLedger, StaffLedger drilldown, CashHold) so we preserve every
// existing feature (Full history modal, add/edit/delete, settlement
// markers, audit trail — from P.11/P.14/P.27.2/P.30) without duplicating
// their code. Adds a "Transfer" button that opens a paired-entry dialog.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowRightLeft, Wallet } from "lucide-react";
import ManagerLedger from "@/pages/ManagerLedger";
import { StaffLedgerDetail } from "@/pages/StaffLedger";
import CashHold from "@/pages/CashHold";

function TransferDialog({ open, onOpenChange, fromWallet, wallets, onDone }) {
  const [form, setForm] = useState({
    from_wallet: fromWallet || "manager",
    to_wallet: "",
    amount: "",
    note: "",
    cash_type: "simple",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm((f) => ({ ...f, from_wallet: fromWallet || f.from_wallet }));
  }, [open, fromWallet]);

  const submit = async () => {
    const amt = Number(form.amount || 0);
    if (amt <= 0) { toast.error("Amount must be positive"); return; }
    if (form.from_wallet === form.to_wallet) { toast.error("Pick two different wallets"); return; }
    if (!form.to_wallet) { toast.error("Choose a destination wallet"); return; }
    setBusy(true);
    try {
      await api.post("/ledger/transfer", {
        from_wallet: form.from_wallet,
        to_wallet: form.to_wallet,
        amount: amt,
        note: form.note,
        cash_type: form.cash_type,
      });
      toast.success(`Transferred ${formatCurrency(amt, "EUR")}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ledger-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" /> Transfer between wallets
          </DialogTitle>
          <DialogDescription>Move cash between the manager wallet and any staff wallet. Creates matched IN/OUT entries linked by transfer id.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">From wallet</Label>
            <Select value={form.from_wallet} onValueChange={(v) => setForm({ ...form, from_wallet: v })}>
              <SelectTrigger data-testid="transfer-from-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {wallets.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">To wallet</Label>
            <Select value={form.to_wallet} onValueChange={(v) => setForm({ ...form, to_wallet: v })}>
              <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Choose destination" /></SelectTrigger>
              <SelectContent>
                {wallets.filter((w) => w.value !== form.from_wallet).map((w) => (
                  <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Amount €</Label>
              <Input type="number" min="0" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="transfer-amount" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.cash_type} onValueChange={(v) => setForm({ ...form, cash_type: v })}>
                <SelectTrigger data-testid="transfer-cash-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">On the book (simple)</SelectItem>
                  <SelectItem value="invoice">Off the book (unbooked)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. petty cash top-up" data-testid="transfer-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-cta" onClick={submit} disabled={busy} data-testid="transfer-submit">
            {busy ? "Transferring…" : "Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Ledger() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  const wallet = params.get("wallet") || "manager";

  useEffect(() => {
    api.get("/staff", { params: { active_only: true } })
      .then((r) => setStaff(r.data || []))
      .catch(() => setStaff([]));
  }, []);

  const wallets = useMemo(() => [
    { value: "manager", label: "Manager Cash" },
    ...staff.map((st) => ({ value: `staff:${st.id}`, label: `Staff · ${st.name}` })),
    { value: "company", label: "Company Cash Hold" },
  ], [staff]);

  const setWallet = (v) => {
    const next = new URLSearchParams(params);
    next.set("wallet", v);
    setParams(next, { replace: false });
  };

  // Inner component switch.
  let body = null;
  if (wallet === "manager") {
    body = <ManagerLedger key={`mgr-${reloadNonce}`} />;
  } else if (wallet === "company") {
    body = <CashHold key={`co-${reloadNonce}`} />;
  } else if (wallet.startsWith("staff:")) {
    const sid = wallet.split(":", 2)[1];
    const st = staff.find((x) => x.id === sid);
    body = st ? <StaffLedgerDetail key={`stf-${sid}-${reloadNonce}`} staffId={sid} staffName={st.name} onBack={() => setWallet("manager")} /> : null;
  }

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-end gap-3 border border-border rounded-lg p-3 bg-background sticky top-14 z-10">
        <div>
          <Label className="text-xs flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Wallet</Label>
          <Select value={wallet} onValueChange={setWallet}>
            <SelectTrigger className="w-64" data-testid="ledger-wallet-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {wallets.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button
          onClick={() => setTransferOpen(true)}
          variant="outline"
          className="gap-2 border-sky-300 text-sky-700 hover:bg-sky-50"
          data-testid="ledger-transfer-btn"
        >
          <ArrowRightLeft className="h-4 w-4" /> Transfer
        </Button>
      </div>

      {body}

      <TransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        fromWallet={wallet === "company" ? "manager" : wallet}
        wallets={wallets}
        onDone={() => setReloadNonce((n) => n + 1)}
      />
    </div>
  );
}
